<template>
    <fieldset :disabled="isDisable">
        <div class="content">
            <div class="d-flex justify-content-between align-items-center mb-3">
                <h4 class="mb-0">{{ $t('Clinical Examination') }}</h4>
                <div class="d-flex" style="gap: 8px;">
                    <a class="btn btn-primary text-white cursor-pointer" @click="downloadReport">
                        <app-icon name="download" /> {{ $t('Download Report') }}
                    </a>
                    <a class="btn btn-primary text-white cursor-pointer" @click="printReport">
                        <app-icon name="printer" /> {{ $t('Print Report') }}
                    </a>
                </div>
            </div>

            <!-- Clinical Examination (restored from production) -->
            <div class="card mb-4">
                <div class="card-body">
                    <div class="row">
                        <div class="col-md-4"><strong>{{ $t('Test') }}</strong></div>
                        <div class="col-md-4"><strong>{{ $t('Left') }}</strong></div>
                        <div class="col-md-4"><strong>{{ $t('Right') }}</strong></div>
                    </div>
                    <hr />
                    <div class="row" v-for="field in examFields" :key="field.id">
                        <div class="col-md-4 d-flex align-items-center mb-2">
                            <label class="mb-0">{{ field.name }}</label>
                        </div>
                        <template v-if="field.name === 'Leg length discrepancy'">
                            <div class="col-md-4 mb-2">
                                <input type="text" class="form-control"
                                    v-model="examValues[field.id].leg_length_discrepancy_left"
                                    :placeholder="$t('Enter Leg length discrepancy...')" />
                            </div>
                            <div class="col-md-4 mb-2">
                                <input type="text" class="form-control"
                                    v-model="examValues[field.id].leg_length_discrepancy_right"
                                    :placeholder="$t('Enter Leg length discrepancy...')" />
                            </div>
                        </template>
                        <template v-else>
                            <div class="col-md-4 mb-2">
                                <select class="form-control" v-model="examValues[field.id].left">
                                    <option :value="null">{{ $t('Select') }} {{ field.name }}</option>
                                    <option v-for="option in getDropdownOptions(field.id)" :key="option.id"
                                        :value="option.id">{{ option.value }}</option>
                                </select>
                            </div>
                            <div class="col-md-4 mb-2">
                                <select class="form-control" v-model="examValues[field.id].right">
                                    <option :value="null">{{ $t('Select') }} {{ field.name }}</option>
                                    <option v-for="option in getDropdownOptions(field.id)" :key="option.id"
                                        :value="option.id">{{ option.value }}</option>
                                </select>
                            </div>
                        </template>
                    </div>
                    <div class="form-group mt-2">
                        <label for="examComments">{{ $t('Comments') }}:</label>
                        <textarea class="form-control" id="examComments" rows="4" v-model="examComments"
                            :placeholder="$t('Enter comments')"></textarea>
                        <div class="mt-1" v-if="!isDisable">
                            <ai-refine-button :text="examComments" context="Clinical examination comments"
                                @apply="val => examComments = val" />
                        </div>
                    </div>
                    <div class="d-flex align-items-center justify-content-between">
                        <div class="form-check">
                            <input type="checkbox" class="form-check-input" id="examAddToReport"
                                v-model="examAddToReport">
                            <label class="form-check-label" for="examAddToReport">{{ $t('Add to Report') }}</label>
                        </div>
                        <button class="btn btn-primary" v-if="!isDisable" type="button"
                            @click="saveClinicalExamination">{{ $t('save') }}</button>
                    </div>
                </div>
            </div>

            <!-- Upload video (before External video links) -->
            <div class="card mb-4">
                <div class="card-body">
                    <h5 class="mb-3"><b>{{ $t('Upload video') }}</b></h5>
                    <exam-video-slot v-for="n in 3" :key="'uv' + n" :slot="n" :customer-id="props[0]"
                        :url="videoUrls[n]" :title="videoTitles[n]" :disabled="isDisable"
                        @update:url="val => $set(videoUrls, n, val)"
                        @title-change="val => $set(videoTitles, n, val)" />
                </div>
            </div>

            <!-- Video links (between Examination and Assessment sections) -->
            <div class="card mb-4">
                <div class="card-body">
                    <h5 class="mb-3"><b>{{ $t('External video links') }}</b></h5>
                    <div class="row align-items-center mb-2" v-for="n in 3" :key="'video' + n">
                        <div class="col-md-2">
                            <label class="mb-0">{{ $t('Video') }} {{ n }}</label>
                        </div>
                        <div class="col-md-8">
                            <input type="url" class="form-control" v-model="videoLinks[n]"
                                :placeholder="$t('Paste video link (Google Drive, Dropbox, etc.)')" />
                        </div>
                        <div class="col-md-2">
                            <a v-if="videoLinks[n]" :href="videoHref(n)" target="_blank" rel="noopener"
                                class="btn btn-outline-primary btn-sm">
                                <app-icon name="external-link" /> {{ $t('Open') }}
                            </a>
                        </div>
                    </div>
                    <div class="d-flex align-items-center justify-content-between mt-2">
                        <div class="form-check">
                            <input type="checkbox" class="form-check-input" id="videosAddToReport"
                                v-model="videosAddToReport">
                            <label class="form-check-label" for="videosAddToReport">{{ $t('Add to Report') }}</label>
                        </div>
                        <button class="btn btn-primary" v-if="!isDisable" type="button"
                            @click="saveClinicalExamination">{{ $t('save') }}</button>
                    </div>
                </div>
            </div>

            <div v-if="loading" class="text-muted py-4">{{ $t('Loading...') }}</div>

            <div v-for="(a, index) in assessments" :key="a._key" class="card mb-4">
                <div class="card-body">
                    <div class="d-flex justify-content-between align-items-center mb-3">
                        <strong>{{ $t('Assessment') }} {{ index + 1 }}</strong>
                        <button v-if="!isDisable" class="btn btn-sm btn-outline-danger" type="button"
                            @click="deleteAssessment(index)">
                            <app-icon name="trash-2" /> {{ $t('Remove') }}
                        </button>
                    </div>

                    <div class="row">
                        <div class="col-12 col-md-6 mb-2">
                            <label>{{ $t('Assessment Title') }} <span class="text-danger">*</span></label>
                            <select class="form-control" v-model="a.list_item_id">
                                <option value="">{{ $t('Select Title') }}</option>
                                <option v-for="t in titleOptions" :key="t.id" :value="t.id">{{ t.item_label }}</option>
                                <option value="custom">{{ $t('Custom') }}</option>
                            </select>
                        </div>
                        <div class="col-12 col-md-6 mb-2" v-if="a.list_item_id === 'custom'">
                            <label>{{ $t('Custom Title') }} <span class="text-danger">*</span></label>
                            <input type="text" class="form-control" v-model="a.custom_title"
                                :placeholder="$t('Enter custom title')" />
                        </div>
                        <div class="col-12 col-md-6 mb-2">
                            <app-form-group type="date" :label="$t('Examination Date') + ' *'"
                                v-model="a.examination_date" :min-date="minExamDate" :max-date="maxExamDate"
                                :placeholder="'dd-mm-yyyy'" />
                        </div>
                    </div>

                    <div class="row">
                        <div class="col-12 col-md-6 mb-2">
                            <label>{{ $t('Image 1') }} <span class="text-danger">*</span></label>
                            <input v-if="!isDisable" type="file" class="form-control" accept="image/*"
                                @change="onImageChange(index, 1, $event)" />
                            <div v-if="a.image_1_preview || a.image_1_url" class="mt-2">
                                <img :src="a.image_1_preview || a.image_1_url" style="max-height:140px; max-width:100%; cursor:zoom-in;"
                                    :title="$t('Click to enlarge')" @click="openLightbox(a.image_1_preview || a.image_1_url)" />
                                <div class="mt-1">
                                    <button type="button" class="btn btn-sm btn-link p-0 mr-2"
                                        :title="$t('Enlarge')" @click="openLightbox(a.image_1_preview || a.image_1_url)"><app-icon name="maximize-2" /></button>
                                    <button type="button" class="btn btn-sm btn-link p-0 mr-2"
                                        :title="$t('Download')" @click="downloadImage(a.image_1_url || a.image_1_preview)"><app-icon name="download" /></button>
                                    <button v-if="!isDisable && (a.image_1 || a.image_1_file)" type="button" class="btn btn-sm btn-link text-danger p-0"
                                        :title="$t('Delete')" @click="removeImage(index, 1)"><app-icon name="trash-2" /></button>
                                </div>
                                <div class="mt-1" v-if="!isDisable && a.id && a.image_1">
                                    <ai-image-analysis-button :assessment-id="a.id" :slot="1"
                                        @insert="val => a.comment = (a.comment ? a.comment + '\n\n' : '') + val" />
                                </div>
                            </div>
                        </div>
                        <div class="col-12 col-md-6 mb-2">
                            <label>{{ $t('Image 2') }}</label>
                            <input v-if="!isDisable" type="file" class="form-control" accept="image/*"
                                @change="onImageChange(index, 2, $event)" />
                            <div v-if="a.image_2_preview || a.image_2_url" class="mt-2">
                                <img :src="a.image_2_preview || a.image_2_url" style="max-height:140px; max-width:100%; cursor:zoom-in;"
                                    :title="$t('Click to enlarge')" @click="openLightbox(a.image_2_preview || a.image_2_url)" />
                                <div class="mt-1">
                                    <button type="button" class="btn btn-sm btn-link p-0 mr-2"
                                        :title="$t('Enlarge')" @click="openLightbox(a.image_2_preview || a.image_2_url)"><app-icon name="maximize-2" /></button>
                                    <button type="button" class="btn btn-sm btn-link p-0 mr-2"
                                        :title="$t('Download')" @click="downloadImage(a.image_2_url || a.image_2_preview)"><app-icon name="download" /></button>
                                    <button v-if="!isDisable && (a.image_2 || a.image_2_file)" type="button" class="btn btn-sm btn-link text-danger p-0"
                                        :title="$t('Delete')" @click="removeImage(index, 2)"><app-icon name="trash-2" /></button>
                                </div>
                                <div class="mt-1" v-if="!isDisable && a.id && a.image_2">
                                    <ai-image-analysis-button :assessment-id="a.id" :slot="2"
                                        @insert="val => a.comment = (a.comment ? a.comment + '\n\n' : '') + val" />
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="row">
                        <div class="col-12 mb-2">
                            <label>{{ $t('Comment') }}</label>
                            <textarea class="form-control" rows="3" v-model="a.comment"
                                :placeholder="$t('Comment')"></textarea>
                            <div class="mt-1" v-if="!isDisable">
                                <ai-refine-button :text="a.comment" context="Assessment comment"
                                    @apply="val => a.comment = val" />
                            </div>
                        </div>
                    </div>

                    <div class="row">
                        <div class="col-12 d-flex align-items-center justify-content-between">
                            <div class="form-check">
                                <input type="checkbox" class="form-check-input" :id="'atr' + index"
                                    v-model="a.add_to_report">
                                <label class="form-check-label" :for="'atr' + index">{{ $t('Add To Report') }}</label>
                            </div>
                            <button v-if="!isDisable" class="btn btn-primary" type="button"
                                @click="saveAssessment(index)">{{ $t('save') }}</button>
                        </div>
                    </div>
                </div>
            </div>

            <div v-if="!isDisable && assessments.length < maxAssessments" class="mb-4">
                <a class="btn btn-outline-primary cursor-pointer" @click="addAssessment">
                    <app-icon name="plus" /> {{ $t('Add Assessment') }}
                </a>
            </div>
            <p v-if="assessments.length >= maxAssessments" class="text-muted">
                {{ $t('Maximum 15 assessments reached.') }}
            </p>
        </div>

        <div v-if="lightboxSrc" @click="closeLightbox"
            style="position:fixed; top:0; left:0; right:0; bottom:0; background:rgba(0,0,0,0.85); display:flex; align-items:center; justify-content:center; z-index:1080; cursor:zoom-out;">
            <img :src="lightboxSrc" style="max-width:92%; max-height:92%; box-shadow:0 0 24px rgba(0,0,0,0.6);" />
        </div>
    </fieldset>
</template>

<script>
import { axiosGet, axiosPost, axiosDelete, urlGenerator } from "../../../../../common/Helper/AxiosHelper";
import AiRefineButton from "./AiRefineButton.vue";
import AiImageAnalysisButton from "./AiImageAnalysisButton.vue";
import ExamVideoSlot from "./ExamVideoSlot.vue";

export default {
    name: "Examination",
    components: { AiRefineButton, AiImageAnalysisButton, ExamVideoSlot },
    props: {
        props: {},
    },
    data() {
        return {
            assessments: [],
            titleOptions: [],
            maxAssessments: 15,
            isDisable: false,
            loading: true,
            urlGenerator,
            keySeq: 0,
            lightboxSrc: null,
            // Clinical Examination (restored)
            examFields: [],
            examDropdownOptions: {},
            examValues: {},
            examComments: '',
            examAddToReport: false,
            videoLinks: { 1: '', 2: '', 3: '' },
            videosAddToReport: false,
            videoUrls: { 1: null, 2: null, 3: null },
            videoTitles: { 1: '', 2: '', 3: '' },
        };
    },
    created() {
        this.isDisable = this.props[1];
        this.fetchTitles();
        this.fetchAssessments();
        this.loadClinicalExamination();
    },
    computed: {
        // Examination Date is limited to today and the 3 preceding days.
        maxExamDate() {
            return new Date();
        },
        minExamDate() {
            const d = new Date();
            d.setDate(d.getDate() - 3);
            return d;
        },
    },
    methods: {
        // ---- Clinical Examination (restored from production) ----
        getDropdownOptions(fieldId) {
            return this.examDropdownOptions[fieldId] || [];
        },
        loadClinicalExamination() {
            axiosGet('app/fields').then((res) => {
                const fields = Array.isArray(res.data) ? res.data : [];
                const vals = {};
                fields.forEach((f) => {
                    vals[f.id] = {
                        left: null,
                        right: null,
                        leg_length_discrepancy_left: null,
                        leg_length_discrepancy_right: null,
                    };
                });
                this.examValues = vals;
                this.examFields = fields;
                return axiosGet('app/dropdown_options');
            }).then((res) => {
                const rows = Array.isArray(res.data) ? res.data : [];
                const grouped = {};
                rows.forEach((o) => {
                    if (!grouped[o.field_id]) grouped[o.field_id] = [];
                    grouped[o.field_id].push({ id: o.id, value: o.value });
                });
                this.examDropdownOptions = grouped;
                return axiosGet('app/fetch-all-examination-data/' + this.props[0]);
            }).then((res) => {
                const ed = (res.data && res.data.examination_data) ? res.data.examination_data : {};
                this.examAddToReport = !!ed.examinations_add_to_report;
                this.examComments = ed.examination_fields_comments || '';
                this.videoLinks = {
                    1: ed.video_link_1 || '',
                    2: ed.video_link_2 || '',
                    3: ed.video_link_3 || '',
                };
                this.videosAddToReport = !!ed.videos_add_to_report;
                this.videoUrls = { 1: ed.video_url_1 || null, 2: ed.video_url_2 || null, 3: ed.video_url_3 || null };
                this.videoTitles = { 1: ed.video_title_1 || '', 2: ed.video_title_2 || '', 3: ed.video_title_3 || '' };
                const saved = Array.isArray(ed.examinations) ? ed.examinations : [];
                saved.forEach((d) => {
                    if (this.examValues[d.field_id]) {
                        this.examValues[d.field_id].left = d.left_option_id;
                        this.examValues[d.field_id].right = d.right_option_id;
                        this.examValues[d.field_id].leg_length_discrepancy_left = d.leg_length_discrepancy_left;
                        this.examValues[d.field_id].leg_length_discrepancy_right = d.leg_length_discrepancy_right;
                    }
                });
            }).catch((e) => {
                console.error('Clinical Examination load failed', e);
            });
        },
        saveClinicalExamination() {
            const relatedData = this.examFields.map((f) => {
                const v = this.examValues[f.id] || {};
                return {
                    customer_id: this.props[0],
                    field_id: f.id,
                    left_option_id: v.left,
                    right_option_id: v.right,
                    leg_length_discrepancy_left: v.leg_length_discrepancy_left,
                    leg_length_discrepancy_right: v.leg_length_discrepancy_right,
                };
            });
            const data = {
                relatedData,
                exAddToReport: this.examAddToReport,
                examination_fields_comments: this.examComments,
                video_link_1: this.videoLinks[1],
                video_link_2: this.videoLinks[2],
                video_link_3: this.videoLinks[3],
                videos_add_to_report: this.videosAddToReport,
                video_title_1: this.videoTitles[1],
                video_title_2: this.videoTitles[2],
                video_title_3: this.videoTitles[3],
            };
            axiosPost('app/examinations/' + this.props[0], data).then((res) => {
                this.$toastr.s('', (res.data && res.data.message) || 'Saved');
            }).catch((err) => {
                const msg = err.response && err.response.data && err.response.data.message
                    ? err.response.data.message : 'Error saving examination';
                this.$toastr.e(msg);
            });
        },
        // ---- End Clinical Examination ----
        newRow(data) {
            data = data || {};
            return {
                _key: 'row-' + (this.keySeq++),
                id: data.id || null,
                list_item_id: data.list_item_id || (data.assessment_title ? 'custom' : ''),
                custom_title: (!data.list_item_id && data.assessment_title) ? data.assessment_title : '',
                // New assessments default to today; existing ones keep their saved date.
                examination_date: data.examination_date || new Date(),
                comment: data.comment || '',
                add_to_report: !!data.add_to_report,
                image_1: data.image_1 || null,
                image_2: data.image_2 || null,
                image_1_url: data.image_1_url || null,
                image_2_url: data.image_2_url || null,
                image_1_file: null,
                image_2_file: null,
                image_1_preview: null,
                image_2_preview: null,
                remove_image_1: false,
                remove_image_2: false,
            };
        },
        fetchTitles() {
            axiosGet('app/selectable-assessment-titles').then((res) => {
                const items = (res.data && res.data.data) ? res.data.data : res.data;
                this.titleOptions = Array.isArray(items) ? items : [];
            }).catch(() => { });
        },
        fetchAssessments() {
            this.loading = true;
            axiosGet(`app/customer-assessments/${this.props[0]}`).then((res) => {
                const rows = (res.data && res.data.data) ? res.data.data : res.data;
                this.assessments = (Array.isArray(rows) ? rows : []).map((r) => this.newRow(r));
            }).catch((e) => {
                console.error(e);
            }).finally(() => {
                this.loading = false;
            });
        },
        addAssessment() {
            if (this.assessments.length >= this.maxAssessments) return;
            this.assessments.push(this.newRow());
        },
        onImageChange(index, slot, event) {
            const file = event.target.files[0];
            if (!file) return;
            const a = this.assessments[index];
            a['image_' + slot + '_file'] = file;
            a['image_' + slot + '_preview'] = URL.createObjectURL(file);
            a['remove_image_' + slot] = false;
        },
        openLightbox(src) {
            if (src) this.lightboxSrc = src;
        },
        closeLightbox() {
            this.lightboxSrc = null;
        },
        downloadImage(src) {
            if (!src) return;
            const name = src.split('/').pop().split('?')[0] || 'image';
            const link = document.createElement('a');
            link.href = src;
            link.setAttribute('download', name);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        },
        removeImage(index, slot) {
            const a = this.assessments[index];
            a['image_' + slot + '_file'] = null;
            a['image_' + slot + '_preview'] = null;
            a['image_' + slot + '_url'] = null;
            a['image_' + slot] = null;
            a['remove_image_' + slot] = true;
        },
        toYmd(v) {
            if (!v) return '';
            if (v instanceof Date) {
                const p = (n) => String(n).padStart(2, '0');
                return `${v.getFullYear()}-${p(v.getMonth() + 1)}-${p(v.getDate())}`;
            }
            const m = /^(\d{2})-(\d{2})-(\d{4})$/.exec(String(v));
            if (m) return `${m[3]}-${m[2]}-${m[1]}`;
            return String(v);
        },
        saveAssessment(index) {
            const a = this.assessments[index];

            // Required-field validation
            if (!a.list_item_id) {
                this.$toastr.e(this.$t('Assessment Title is required.'));
                return;
            }
            if (a.list_item_id === 'custom' && !(a.custom_title || '').trim()) {
                this.$toastr.e(this.$t('Custom Title is required.'));
                return;
            }
            const examDate = this.toYmd(a.examination_date);
            if (!examDate) {
                this.$toastr.e(this.$t('Examination Date is required.'));
                return;
            }
            const hasImage1 = !!a.image_1_file || (!!a.image_1 && !a.remove_image_1);
            if (!hasImage1) {
                this.$toastr.e(this.$t('Image 1 is required.'));
                return;
            }

            const fd = new FormData();
            if (a.id) fd.append('id', a.id);
            fd.append('customer_id', this.props[0]);
            if (a.list_item_id && a.list_item_id !== 'custom') {
                fd.append('list_item_id', a.list_item_id);
            } else if (a.list_item_id === 'custom') {
                fd.append('custom_title', a.custom_title || '');
            }
            fd.append('examination_date', examDate);
            fd.append('comment', a.comment || '');
            fd.append('add_to_report', a.add_to_report ? 1 : 0);
            fd.append('sort_order', index);
            if (a.image_1_file) fd.append('image_1', a.image_1_file);
            if (a.image_2_file) fd.append('image_2', a.image_2_file);
            if (a.remove_image_1) fd.append('remove_image_1', 1);
            if (a.remove_image_2) fd.append('remove_image_2', 1);

            axiosPost('app/customer-assessments', fd).then((res) => {
                this.$toastr.s('', (res.data && res.data.message) || 'Saved');
                this.fetchAssessments();
            }).catch((err) => {
                const msg = err.response && err.response.data && err.response.data.message
                    ? err.response.data.message : 'Error saving assessment';
                this.$toastr.e(msg);
            });
        },
        deleteAssessment(index) {
            const a = this.assessments[index];
            if (!a.id) {
                this.assessments.splice(index, 1);
                return;
            }
            if (!confirm(this.$t('Are you sure you want to delete this assessment?'))) return;
            axiosDelete(`app/customer-assessments/${a.id}`).then((res) => {
                this.$toastr.s('', (res.data && res.data.message) || 'Deleted');
                this.assessments.splice(index, 1);
            }).catch(() => {
                this.$toastr.e('Error deleting assessment');
            });
        },
        downloadReport() {
            const url = urlGenerator('app/examination/print/' + this.props[0]);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', '');
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        },
        videoHref(n) {
            // A link like "amazon.in" (no scheme) is otherwise treated as a relative
            // path → resolves under the app URL. Prepend https:// when no scheme is present.
            const url = (this.videoLinks[n] || '').trim();
            if (!url) return '#';
            return /^https?:\/\//i.test(url) ? url : 'https://' + url;
        },
        printReport() {
            // Opens the report as an auto-printing HTML page → the browser Print dialog.
            const url = urlGenerator('app/examination/print/' + this.props[0] + '?mode=print');
            window.open(url, '_blank');
        },
    },
};
</script>

<style lang="scss" scoped>
/* Keep anchor-style buttons (e.g. Download Report) clickable even when the
   surrounding fieldset is disabled in View mode. */
fieldset:disabled a.btn {
    pointer-events: all;
}
</style>
